import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  ButtonGroup,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import { 
  CalendarToday, 
  DateRange,
  BarChart,
  PieChart,
  TableChart,
  Download,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Reports: React.FC = () => {
  const [dateRange, setDateRange] = useState<'today' | 'yesterday' | 'last7days' | 'specific' | 'custom'>('today');
  const [specificDate, setSpecificDate] = useState<Date | null>(new Date());
  const [startDate, setStartDate] = useState<Date | null>(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  interface ServiceRevenue {
    name: string;
    amount: number;
  }
  
  interface StaffRevenue {
    name: string;
    amount: number;
  }
  
  interface DayAppointment {
    day: string;
    count: number;
  }
  
  interface ServiceAppointment {
    name: string;
    count: number;
  }
  
  interface ClientSpender {
    name: string;
    spent: number;
    visits: number;
  }
  
  const [reportData, setReportData] = useState({
    revenue: {
      total: 0,
      services: 0,
      products: 0,
      tips: 0,
      byService: [] as ServiceRevenue[],
      byStaff: [] as StaffRevenue[],
    },
    appointments: {
      total: 0,
      completed: 0,
      cancelled: 0,
      byDay: [] as DayAppointment[],
      byService: [] as ServiceAppointment[],
    },
    clients: {
      total: 0,
      new: 0,
      returning: 0,
      topSpenders: [] as ClientSpender[],
    },
  });

  useEffect(() => {
    fetchReportData();
  }, [dateRange, specificDate, startDate, endDate, tabValue]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      let start: Date, end: Date;
      
      switch (dateRange) {
        case 'today':
          start = startOfDay(new Date());
          end = endOfDay(new Date());
          break;
        case 'yesterday':
          start = startOfDay(subDays(new Date(), 1));
          end = endOfDay(subDays(new Date(), 1));
          break;
        case 'last7days':
          start = startOfDay(subDays(new Date(), 6));
          end = endOfDay(new Date());
          break;
        case 'specific':
          if (specificDate) {
            start = startOfDay(specificDate);
            end = endOfDay(specificDate);
          } else {
            start = startOfDay(new Date());
            end = endOfDay(new Date());
          }
          break;
        case 'custom':
          if (startDate && endDate) {
            start = startOfDay(startDate);
            end = endOfDay(endDate);
          } else {
            start = startOfDay(subDays(new Date(), 7));
            end = endOfDay(new Date());
          }
          break;
        default:
          start = startOfDay(new Date());
          end = endOfDay(new Date());
      }

      // Format dates for API
      const startStr = format(start, 'yyyy-MM-dd');
      const endStr = format(end, 'yyyy-MM-dd');

      // Fetch real data from API
      const [appointmentsRes, clientsRes] = await Promise.all([
        fetch('https://salon.rockyroadsolutions.com/api/appointments'),
        fetch('https://salon.rockyroadsolutions.com/api/clients')
      ]);
      
      const appointments = appointmentsRes.ok ? await appointmentsRes.json() : [];
      const clients = clientsRes.ok ? await clientsRes.json() : [];
      
      // Filter appointments by date range
      const filteredAppointments = appointments.filter((apt: any) => {
        const aptDate = new Date(apt.appointmentDate);
        return aptDate >= start && aptDate <= end;
      });
      
      // Calculate revenue data
      const totalRevenue = filteredAppointments.reduce((sum: number, apt: any) => 
        sum + parseFloat(apt.totalPrice || 0) + parseFloat(apt.tipAmount || 0), 0
      );
      
      const serviceRevenue = filteredAppointments.reduce((acc: any, apt: any) => {
        const serviceName = apt.service_names || 'Service';
        const amount = parseFloat(apt.totalPrice || 0) + parseFloat(apt.tipAmount || 0);
        acc[serviceName] = (acc[serviceName] || 0) + amount;
        return acc;
      }, {});
      
      const staffRevenue = filteredAppointments.reduce((acc: any, apt: any) => {
        const staffName = `${apt.primaryStaff?.firstName || ''} ${apt.primaryStaff?.lastName || ''}`.trim() || 'Unknown Staff';
        const amount = parseFloat(apt.totalPrice || 0) + parseFloat(apt.tipAmount || 0);
        acc[staffName] = (acc[staffName] || 0) + amount;
        return acc;
      }, {});
      
      // Calculate appointment stats
      const completedAppointments = filteredAppointments.filter((apt: any) => apt.status === 'COMPLETED').length;
      const cancelledAppointments = filteredAppointments.filter((apt: any) => apt.status === 'CANCELLED').length;
      
      const appointmentsByDay = filteredAppointments.reduce((acc: any, apt: any) => {
        const day = new Date(apt.appointmentDate).toLocaleDateString('en-US', { weekday: 'long' });
        acc[day] = (acc[day] || 0) + 1;
        return acc;
      }, {});
      
      const appointmentsByService = filteredAppointments.reduce((acc: any, apt: any) => {
        const serviceName = apt.service_names || 'Service';
        acc[serviceName] = (acc[serviceName] || 0) + 1;
        return acc;
      }, {});
      
      // Calculate client stats
      const clientSpending = filteredAppointments.reduce((acc: any, apt: any) => {
        const clientName = `${apt.client?.firstName || ''} ${apt.client?.lastName || ''}`.trim() || 'Unknown Client';
        const amount = parseFloat(apt.totalPrice || 0) + parseFloat(apt.tipAmount || 0);
        if (!acc[clientName]) {
          acc[clientName] = { spent: 0, visits: 0 };
        }
        acc[clientName].spent += amount;
        acc[clientName].visits += 1;
        return acc;
      }, {});
      
      const topSpenders = Object.entries(clientSpending)
        .map(([name, data]: [string, any]) => ({ name, spent: data.spent, visits: data.visits }))
        .sort((a, b) => b.spent - a.spent)
        .slice(0, 5);
      
      const totalTips = filteredAppointments.reduce((sum: number, apt: any) => 
        sum + parseFloat(apt.tipAmount || 0), 0
      );
      
      const totalServices = filteredAppointments.reduce((sum: number, apt: any) => 
        sum + parseFloat(apt.totalPrice || 0), 0
      );
      
      setReportData({
        revenue: {
          total: totalRevenue,
          services: totalServices,
          products: 0,
          tips: totalTips,
          byService: Object.entries(serviceRevenue).map(([name, amount]) => ({ name, amount: amount as number })),
          byStaff: Object.entries(staffRevenue).map(([name, amount]) => ({ name, amount: amount as number }))
        },
        appointments: {
          total: filteredAppointments.length,
          completed: completedAppointments,
          cancelled: cancelledAppointments,
          byDay: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
            .map(day => ({ day, count: appointmentsByDay[day] || 0 })),
          byService: Object.entries(appointmentsByService).map(([name, count]) => ({ name, count: count as number }))
        },
        clients: {
          total: clients.length,
          new: clients.filter((c: any) => {
            const joinDate = new Date(c.created_at || c.createdAt);
            return joinDate >= start && joinDate <= end;
          }).length,
          returning: clients.length - clients.filter((c: any) => {
            const joinDate = new Date(c.created_at || c.createdAt);
            return joinDate >= start && joinDate <= end;
          }).length,
          topSpenders
        }
      });
      
      setLoading(false);
      
    } catch (error) {
      console.error('Error fetching report data:', error);
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getDateRangeText = () => {
    switch (dateRange) {
      case 'today':
        return 'Today';
      case 'yesterday':
        return 'Yesterday';
      case 'last7days':
        return 'Last 7 Days';
      case 'specific':
        return specificDate ? format(specificDate, 'MMM dd, yyyy') : 'Specific Date';
      case 'custom':
        return startDate && endDate 
          ? `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
          : 'Custom Range';
      default:
        return 'Today';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Reports
        </Typography>
        <Box>
          <ButtonGroup variant="outlined" size="small" sx={{ mr: 2 }}>
            <Button 
              onClick={() => setDateRange('today')}
              variant={dateRange === 'today' ? 'contained' : 'outlined'}
              sx={{ 
                bgcolor: dateRange === 'today' ? 'primary.main' : 'transparent',
                color: dateRange === 'today' ? 'white' : 'inherit',
              }}
            >
              Today
            </Button>
            <Button 
              onClick={() => setDateRange('yesterday')}
              variant={dateRange === 'yesterday' ? 'contained' : 'outlined'}
              sx={{ 
                bgcolor: dateRange === 'yesterday' ? 'primary.main' : 'transparent',
                color: dateRange === 'yesterday' ? 'white' : 'inherit',
              }}
            >
              Yesterday
            </Button>
            <Button 
              onClick={() => setDateRange('last7days')}
              variant={dateRange === 'last7days' ? 'contained' : 'outlined'}
              sx={{ 
                bgcolor: dateRange === 'last7days' ? 'primary.main' : 'transparent',
                color: dateRange === 'last7days' ? 'white' : 'inherit',
              }}
            >
              Last 7 Days
            </Button>
          </ButtonGroup>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {dateRange === 'specific' ? (
              <DatePicker
                label="Specific Date"
                value={specificDate}
                onChange={(newValue) => setSpecificDate(newValue)}
                slotProps={{ textField: { size: 'small', sx: { width: 150, mt: 2 } } }}
              />
            ) : dateRange === 'custom' ? (
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
                />
              </Box>
            ) : (
              <ButtonGroup variant="outlined" size="small">
                <Button 
                  onClick={() => setDateRange('specific')}
                  startIcon={<CalendarToday />}
                >
                  Specific Date
                </Button>
                <Button 
                  onClick={() => setDateRange('custom')}
                  startIcon={<DateRange />}
                >
                  Custom Range
                </Button>
              </ButtonGroup>
            )}
          </LocalizationProvider>
        </Box>
      </Box>

      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
        Showing data for: <strong>{getDateRangeText()}</strong>
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="report tabs"
          sx={{
            '& .MuiTab-root': {
              minWidth: 120,
            }
          }}
        >
          <Tab 
            icon={<BarChart fontSize="small" />} 
            iconPosition="start" 
            label="Revenue" 
            id="report-tab-0" 
            aria-controls="report-tabpanel-0" 
          />
          <Tab 
            icon={<PieChart fontSize="small" />} 
            iconPosition="start" 
            label="Appointments" 
            id="report-tab-1" 
            aria-controls="report-tabpanel-1" 
          />
          <Tab 
            icon={<TableChart fontSize="small" />} 
            iconPosition="start" 
            label="Clients" 
            id="report-tab-2" 
            aria-controls="report-tabpanel-2" 
          />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Revenue Report */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button 
                variant="outlined" 
                startIcon={<Download />}
                onClick={() => alert('Download revenue report')}
              >
                Export Report
              </Button>
            </Box>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600}>Total Revenue</Typography>
                    <Typography variant="h3" fontWeight={700} sx={{ color: '#E91E63', my: 2 }}>
                      ${reportData.revenue.total.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Services</Typography>
                        <Typography variant="h6" fontWeight={600}>${reportData.revenue.services.toLocaleString()}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Tips</Typography>
                        <Typography variant="h6" fontWeight={600}>${reportData.revenue.tips.toLocaleString()}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Products</Typography>
                        <Typography variant="h6" fontWeight={600}>${reportData.revenue.products.toLocaleString()}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Revenue by Service</Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Service</TableCell>
                            <TableCell align="right">Revenue</TableCell>
                            <TableCell align="right">% of Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.revenue.byService.map((service, index) => (
                            <TableRow key={index}>
                              <TableCell>{service.name}</TableCell>
                              <TableCell align="right">${service.amount.toLocaleString()}</TableCell>
                              <TableCell align="right">
                                {((service.amount / reportData.revenue.total) * 100).toFixed(1)}%
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Revenue by Staff</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Staff Member</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                        <TableCell align="right">% of Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.revenue.byStaff.map((staff, index) => (
                        <TableRow key={index}>
                          <TableCell>{staff.name}</TableCell>
                          <TableCell align="right">${staff.amount.toLocaleString()}</TableCell>
                          <TableCell align="right">
                            {((staff.amount / reportData.revenue.total) * 100).toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </TabPanel>

          {/* Appointments Report */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button 
                variant="outlined" 
                startIcon={<Download />}
                onClick={() => alert('Download appointments report')}
              >
                Export Report
              </Button>
            </Box>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600}>Total Appointments</Typography>
                    <Typography variant="h3" fontWeight={700} sx={{ color: '#9C27B0', my: 2 }}>
                      {reportData.appointments.total}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Completed</Typography>
                        <Typography variant="h6" fontWeight={600} sx={{ color: '#4CAF50' }}>
                          {reportData.appointments.completed}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Cancelled</Typography>
                        <Typography variant="h6" fontWeight={600} sx={{ color: '#F44336' }}>
                          {reportData.appointments.cancelled}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Appointments by Day</Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Day</TableCell>
                            <TableCell align="right">Appointments</TableCell>
                            <TableCell align="right">% of Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.appointments.byDay.map((day, index) => (
                            <TableRow key={index}>
                              <TableCell>{day.day}</TableCell>
                              <TableCell align="right">{day.count}</TableCell>
                              <TableCell align="right">
                                {((day.count / reportData.appointments.total) * 100).toFixed(1)}%
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Appointments by Service</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Service</TableCell>
                        <TableCell align="right">Appointments</TableCell>
                        <TableCell align="right">% of Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.appointments.byService.map((service, index) => (
                        <TableRow key={index}>
                          <TableCell>{service.name}</TableCell>
                          <TableCell align="right">{service.count}</TableCell>
                          <TableCell align="right">
                            {((service.count / reportData.appointments.total) * 100).toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </TabPanel>

          {/* Clients Report */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button 
                variant="outlined" 
                startIcon={<Download />}
                onClick={() => alert('Download clients report')}
              >
                Export Report
              </Button>
            </Box>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600}>Total Clients</Typography>
                    <Typography variant="h3" fontWeight={700} sx={{ color: '#2196F3', my: 2 }}>
                      {reportData.clients.total}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">New</Typography>
                        <Typography variant="h6" fontWeight={600} sx={{ color: '#4CAF50' }}>
                          {reportData.clients.new}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Returning</Typography>
                        <Typography variant="h6" fontWeight={600} sx={{ color: '#FF9800' }}>
                          {reportData.clients.returning}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={8}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Top Spenders</Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Client</TableCell>
                            <TableCell align="right">Total Spent</TableCell>
                            <TableCell align="right">Visits</TableCell>
                            <TableCell align="right">Avg. per Visit</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.clients.topSpenders.map((client, index) => (
                            <TableRow key={index}>
                              <TableCell>{client.name}</TableCell>
                              <TableCell align="right">${client.spent.toLocaleString()}</TableCell>
                              <TableCell align="right">{client.visits}</TableCell>
                              <TableCell align="right">
                                ${(client.spent / client.visits).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </>
      )}
    </Box>
  );
};

export default Reports;
